import { Box, Grid } from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import UniversalToolBarWithDialogs from "../../components/bar/UniversalToolBarWithDialogs";
import { TextFilter } from "../../components/common/TextFilter";
import { AddReservationDialog } from "../../components/dialog/AddReservationDialog/AddReservationDialog";
import FilterTable from "../../components/table/FilterTable";
import DefaultPageWrapper from "../../components/wrapper/DefaultPageWrapper";
import LoaderWrapper from "../../components/wrapper/LoaderWrapper";
import { useDialogBetter } from "../../hooks/useDialogBetter";
import usePaginationWithSearchParams from "../../hooks/usePaginationWithSearchParams";
import { useBookings } from "../../services/bookingsService";
import TableService from "../../services/tableService";
import { BOOKINGS_TABLE_NAME, useBookingTableFieldsConfig } from "./BookingPageTableConfig";

export function BookingPage(props) {
    const { pageName } = props;
    const { t } = useTranslation();
    const [hasEditPermission] = [true];

    const [dataLocal, setDataLocal] = useState([]);
    const [recordsCount, setRecordsCount] = useState(0);
    const [selectedRowId, setSelectedRowId] = useState();
    const [hiddenColumnsForTables, setHiddenColumnsForTables] = useState([]);
    const [columnsOrdersForTables, setColumnsOrdersForTables] = useState([]);
    const [checkedAll, setCheckedAll] = useState(false);
    const [tableLoading, setTableLoading] = useState(false);

    const { openDialog: openAddReservationsDialog } = useDialogBetter();

    const {
        resetPageNumber,
        searchParams,
        setSearchParams,
        page: dataPage,
        pageSize: dataPageSize,
        handleChangePageWithSearchParams: handleChangeDataPage,
        handleChangePageSizeWithSearchParams: handleChangeDataPageSize,
    } = usePaginationWithSearchParams();

    const {
        data: bookingsData,
        isLoading: isBookingsLoading,
        refetch,
    } = useBookings(searchParams, {
        select: (data) => data,
        queryKey: ["booking-list"],
    });

    useEffect(() => {
        if (bookingsData?.results) {
            setDataLocal(bookingsData.results);
            setRecordsCount(bookingsData.count);
        }
    }, [bookingsData, dataPage, dataPageSize]);

    const bookingsTableFieldsConfig = useBookingTableFieldsConfig();

    const bookingDataExportHeaders = useMemo(() => {
        return TableService.getTableExportHeaders(
            bookingsTableFieldsConfig, // @ts-ignore
            hiddenColumnsForTables?.[BOOKINGS_TABLE_NAME], // @ts-ignore
            columnsOrdersForTables?.[BOOKINGS_TABLE_NAME],
        );
    }, [
        // @ts-ignore
        hiddenColumnsForTables?.[BOOKINGS_TABLE_NAME],
        // @ts-ignore
        columnsOrdersForTables?.[BOOKINGS_TABLE_NAME],
        bookingsTableFieldsConfig,
    ]);

    // @ts-ignore
    const onClickButton = useCallback((e, id) => {
        e.stopPropagation();
    }, []);

    useEffect(() => {
        const fetchData = async () => {
            setTableLoading(true);
            await refetch();
            setTableLoading(false);
        };
        fetchData();
    }, [searchParams]);

    const data = useMemo(
        () =>
            TableService.getPreparedDataForCollapsibleTable(
                bookingsTableFieldsConfig,
                dataLocal, // @ts-ignore
                hiddenColumnsForTables?.[BOOKINGS_TABLE_NAME]
                    ? // @ts-ignore
                      hiddenColumnsForTables?.[BOOKINGS_TABLE_NAME]
                    : [], // @ts-ignore
                columnsOrdersForTables?.[BOOKINGS_TABLE_NAME]
                    ? // @ts-ignore
                      columnsOrdersForTables?.[BOOKINGS_TABLE_NAME]
                    : [],
                onClickButton,
            ),
        [
            bookingsTableFieldsConfig,
            dataLocal,
            hiddenColumnsForTables,
            columnsOrdersForTables,
            onClickButton,
        ],
    );

    return (
        // @ts-ignore
        <DefaultPageWrapper tagTitle={t("headers.bookings")}>
            <Grid
                item
                container
                direction="row"
                justifyContent="flex-start"
                alignItems="center"
                rowSpacing={1}
            >
                <Grid item xs={12}>
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "start",
                            width: "40%",
                            marginLeft: "50px",
                        }}
                    >
                        <TextFilter
                            filterKey={"customer"}
                            searchParams={searchParams}
                            setSearchParams={setSearchParams}
                            withPagination
                            resetPageNumber={resetPageNumber}
                            placeholder={t("clients.filter.masterFilterPlaceholder") ?? undefined}
                        />
                    </Box>
                </Grid>
                <Grid item xs={12} marginY={1}>
                    <UniversalToolBarWithDialogs
                        pageName={pageName}
                        tableConfig={{
                            name: BOOKINGS_TABLE_NAME,
                            config: bookingsTableFieldsConfig,
                        }}
                        filteringData={() => {}}
                        refreshTable={refetch}
                        // @ts-ignore
                        resetPageNumber={resetPageNumber}
                        hiddenColumnsForTables={hiddenColumnsForTables}
                        setHiddenColumnsForTables={setHiddenColumnsForTables}
                        columnsOrdersForTables={columnsOrdersForTables}
                        setColumnsOrdersForTables={setColumnsOrdersForTables}
                        showMassActionButton={false}
                        showOpenCloseFilterButton={false}
                        showCleanfiltersButton={true}
                        showMyFiltersButton={true}
                        showSearchButton={true}
                        itemType={"booking"}
                        style={{ marginBlock: "5px" }}
                        showCreateButton={hasEditPermission}
                        onClickCreateItem={() =>
                            openAddReservationsDialog({
                                Component: AddReservationDialog,
                                componentProps: {},
                            })
                        }
                        mdGridButton={hasEditPermission ? 1.25 : 2.75}
                        showImportList={false}
                        showRemoveLeadButton={false}
                    />
                </Grid>
                <Grid item xs={12}>
                    <LoaderWrapper showLoader={isBookingsLoading && !dataLocal.length}>
                        <FilterTable
                            // @ts-ignore
                            data={data}
                            countRecords={recordsCount}
                            resetPageNumber={resetPageNumber}
                            selectedItemId={selectedRowId}
                            style={{ maxHeight: "60vh" }}
                            headersConfig={TableService.orderColumns(
                                TableService.hideHiddenColumns(
                                    bookingsTableFieldsConfig, // @ts-ignore
                                    hiddenColumnsForTables?.[BOOKINGS_TABLE_NAME]
                                        ? // @ts-ignore
                                          hiddenColumnsForTables?.[BOOKINGS_TABLE_NAME]
                                        : [],
                                ), // @ts-ignore
                                columnsOrdersForTables?.[BOOKINGS_TABLE_NAME]
                                    ? // @ts-ignore
                                      columnsOrdersForTables?.[BOOKINGS_TABLE_NAME]
                                    : [],
                            )}
                            showCleanFilterIcon={true}
                            showContextMenu={false}
                            showExportToFileButton={true}
                            exportToFileUrl={"bookings/export/"}
                            exportToFileSearchParams={searchParams}
                            exportToFileHeaders={bookingDataExportHeaders}
                            exportToFileFileName={`${t("bookings.exportFileName")}.xlsx`}
                            showDetailsIcon={false}
                            showCheckbox={false}
                            checkedAll={checkedAll}
                            page={dataPage}
                            onPageChange={handleChangeDataPage}
                            rowsPerPage={dataPageSize}
                            onRowsPerPageChange={handleChangeDataPageSize}
                            setDataLocal={setDataLocal}
                            setCheckedAll={setCheckedAll}
                            setSelectedRowId={setSelectedRowId}
                            isLoading={tableLoading}
                        />
                    </LoaderWrapper>
                </Grid>
            </Grid>
        </DefaultPageWrapper>
    );
}
